import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { Layout } from "./components/Layout";
import { Dashboard } from "./pages/Dashboard";
import { KanbanBoard } from "./pages/KanbanBoard";
import { Sessions } from "./pages/Sessions";
import { SessionDetail } from "./pages/SessionDetail";
import { ActivityFeed } from "./pages/ActivityFeed";
import { Analytics } from "./pages/Analytics";
import { Settings } from "./pages/Settings";
import { FinanzasPage } from "./pages/finanzas/FinanzasPage";
import { NotFound } from "./pages/NotFound";
import { Login } from "./pages/Login";
import Polymarket from "./pages/Polymarket";
import { OsintMonitor } from "./pages/OsintMonitor";
import { useWebSocket } from "./hooks/useWebSocket";
import { useNotifications } from "./hooks/useNotifications";
import { eventBus } from "./lib/eventBus";
import { getAuthToken, setAuthToken, clearAuthToken } from "./lib/api";
import { ACCESO_PERDIDO, vigilarAcceso } from "./lib/accesoPerdido";
import type { WSMessage } from "./lib/types";

export default function App() {
  const [authed, setAuthed] = useState(() => !!getAuthToken());
  const [aviso, setAviso] = useState<string | null>(null);

  // ANTES que useWebSocket: sus efectos corren en este orden, y el primer
  // ticket ya tiene que salir vigilado (lib/accesoPerdido.ts).
  useEffect(() => {
    const quitar = vigilarAcceso();
    const alPerder = () => {
      setAuthed(false);
      setAviso("Tu código de acceso ya no vale (cambió). Entra con el actual.");
    };
    window.addEventListener(ACCESO_PERDIDO, alPerder);
    return () => {
      window.removeEventListener(ACCESO_PERDIDO, alPerder);
      quitar();
    };
  }, []);

  const onMessage = useCallback((msg: WSMessage) => {
    eventBus.publish(msg);
  }, []);

  const { connected } = useWebSocket(onMessage);
  useNotifications();

  const handleLogin = (code: string) => {
    setAuthToken(code);
    setAviso(null);
    setAuthed(true);
  };

  const handleLogout = () => {
    clearAuthToken();
    setAuthed(false);
  };

  if (!authed) {
    return <Login onLogin={handleLogin} aviso={aviso} />;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<Layout wsConnected={connected} onLogout={handleLogout} />}>
          <Route index element={<Dashboard />} />
          <Route path="kanban" element={<KanbanBoard />} />
          <Route path="sessions" element={<Sessions />} />
          <Route path="sessions/:id" element={<SessionDetail />} />
          <Route path="activity" element={<ActivityFeed />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="finanzas" element={<FinanzasPage />} />
          <Route path="settings" element={<Settings />} />
          <Route path="polymarket" element={<Polymarket />} />
          <Route path="osint" element={<OsintMonitor />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
